import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ELEAccordionsPageTypes } from 'components/organisms/LEAccordions';
import { formatDate } from 'utils/date';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { getColumnTitleFromCustomField } from 'utils/table';
import { useGlobalProperty } from 'models/globalPropertyContext';

interface ILegalEntityTableCard {
  data: ITableDataSource;
  handleSelect?: (arg: any) => void;
  handleView?: (arg: string) => void;
  handleEdit?: (arg: string) => void;
  handleDelete?: (arg: string) => void;
  customFields?: ICustomFieldByObjectType[];
  type: ELEAccordionsPageTypes;
  shortDateFormat: string;
  hasDeleteLEPermission: boolean;
  hasManageLEPermission: boolean;
  licenceAuthorityIds: string[];
  selectedLanguage: string | undefined;
}

const LegalEntityTableCard = ({
  data,
  handleSelect,
  handleView,
  handleEdit,
  handleDelete,
  customFields,
  type,
  hasManageLEPermission,
  hasDeleteLEPermission,
  licenceAuthorityIds,
  shortDateFormat,
  selectedLanguage,
}: ILegalEntityTableCard) => {
  const { t } = useTranslations();
  const dataToSelect = data as any;

  const isLicenceAuthorityValid =
    !data.primaryWeaponLicencingAuthorityUniqueId ||
    licenceAuthorityIds.includes(data.primaryWeaponLicencingAuthorityUniqueId) ||
    licenceAuthorityIds.includes(data.secondaryWeaponLicencingAuthorityUniqueId);

  const getCFItem = (customField: { name: string }) => {
    if (dataToSelect?.customFields?.length) {
      const cf = dataToSelect.customFields.find(
        (cField: ILegalEntityCustomField) => cField.customFieldName === customField.name,
      );
      if (cf) {
        if (cf.customFieldType === ECustomFieldType.BOOL && t) {
          if (cf.value === null) {
            return '-';
          }
          return cf.value
            ? t('customFields.value.yes.label')
            : t('customFields.value.no.label');
        }
        if (cf.customFieldType === ECustomFieldType.DATE) {
          return formatDate(cf.value, shortDateFormat);
        }
        return cf.value || '-';
      }
    }
    return '-';
  };

  const customFieldColumns = customFields
    ? customFields
        .filter((customField) => customField.visibleInSearchResults)
        .map((customField) => (
          <Grid sx={{ p: 2 }}>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {getColumnTitleFromCustomField(customField, selectedLanguage)}
              </Typography>
            </Grid>
            {getCFItem(customField)}
          </Grid>
        ))
    : null;

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.primary25}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        maxWidth: 'calc(100vw - 48px)',
      }}
    >
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: colors.primary100,
          px: 2,
          py: 1,
        }}
      >
        <Grid size={{ xs: 12 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {data.firstName} {emptyFieldHolder(data.lastName)}
          </Typography>
        </Grid>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Grid>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntities.individual.accordion.legalEntityTable.displayUniqueId.column')}
          </Typography>
        </Grid>
        <Typography variant="body2">{emptyFieldHolder(data.displayUniqueId)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntities.individual.accordion.legalEntityTable.dateBirth.column')}
        </Typography>
        <Typography variant="body2">
          {data.dateOfBirth !== null
            ? formatDate(data.dateOfBirth, shortDateFormat)
            : formatDate(new Date().toString(), shortDateFormat)}
        </Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Grid>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('legalEntities.individual.accordion.legalEntityTable.city.column')}
          </Typography>
        </Grid>
        {data.city}
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Grid>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t(
              'legalEntities.individual.accordion.legalEntityTable.identityDocumentNumber.column',
            )}
          </Typography>
        </Grid>
        <Typography variant="body2">
          {emptyFieldHolder(data.identityDocumentNumber)}
        </Typography>
      </Grid>
      {customFieldColumns}
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary25}`,
            lg: 'none',
          },
          m: {
            xs: 2,
            lg: 2,
          },
        }}
        spacing={2}
      >
        <Grid size={{ xs: 12, sm: 2 }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('legalEntities.individual.accordion.legalEntityTable.action.column')}
          </Typography>
        </Grid>

        <Grid container columnSpacing={1} sx={{ justifyContent: 'flex-end' }}>
          {type === ELEAccordionsPageTypes.LegalEntity ? (
            <>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ mb: { xs: 1, sm: 0 } }}>
                <Button
                  variant={EButtonVariants.outlined}
                  tableButton
                  label={t(
                    'legalEntities.organization.accordion.legalEntityTable.details.link',
                  )}
                  onClick={() => handleView && handleView(`${data.id}`)}
                  fullWidth
                  size={EButtonSizes.small}
                  id={`action_mobile_view_${data.id}`}
                />
              </Grid>

              {hasManageLEPermission && isLicenceAuthorityValid && (
                <Grid size={{ xs: 12, sm: 4 }} sx={{ mb: { xs: 1, sm: 0 } }}>
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    label={t(
                      'legalEntities.organization.accordion.legalEntityTable.edit.link',
                    )}
                    onClick={() => handleEdit && handleEdit(`${data.id}`)}
                    fullWidth
                    size={EButtonSizes.small}
                    id={`action_mobile_edit_${data.id}`}
                  />
                </Grid>
              )}
              {hasDeleteLEPermission && isLicenceAuthorityValid && (
                <Grid size={{ xs: 12, sm: 4 }} sx={{ mb: { xs: 1, sm: 0 } }}>
                  <Button
                    variant={EButtonVariants.outlined}
                    tableButton
                    label={t(
                      'legalEntities.organization.accordion.legalEntityTable.delete.link',
                    )}
                    onClick={() => handleDelete && handleDelete(`${data.id}`)}
                    permission={[EPermission.DELETE_LE]}
                    fullWidth
                    size={EButtonSizes.small}
                    id={`action_mobile_delete_${data.id}`}
                  />
                </Grid>
              )}
            </>
          ) : (
            <Grid size={{ xs: 12, sm: 9 }} sx={{ mb: { xs: 1, sm: 0 } }}>
              <Button
                label={t('legalEntities.organization.accordion.legalEntityTable.select.link')}
                variant={EButtonVariants.outlined}
                onClick={() =>
                  handleSelect &&
                  handleSelect(
                    type && type === ELEAccordionsPageTypes.SelectLegalEntity
                      ? dataToSelect
                      : `${data.id}`,
                  )
                }
                size={EButtonSizes.small}
                fullWidth
                id={`action_mobile_check_${data.id}`}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { LegalEntityTableCard };
